/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Button, Grid, Icon, Spacer, Spinner } from "@cd2/components"
import { useFileboy } from "@cd2/fileboy-react"
import { H4, P, TextBlock } from "@cd2/text/build/es6"
import { singularize } from "@cd2/tools"
import { doo, pluralize, titleize } from "@cd2/tools/build/es6"
import { IconDefinition } from "@fortawesome/pro-solid-svg-icons"
import format from "date-fns/format"
import React, { useContext, useState } from "react"
import ReactJson from "react-json-view"
import { icons } from "../constants/icons"
import { I365Attendee, I365Recipient } from "../hooks/useApi"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { HoverBranchLoader } from "../hoverLoaders/HoverBranchLoader"
import { HoverCustomerLoader } from "../hoverLoaders/HoverCustomerLoader"
import { HoverDeliveryLoader } from "../hoverLoaders/HoverDeliveryLoader"
import { HoverLeadLoader } from "../hoverLoaders/HoverLeadsLoader"
import { HoverOrderLoader } from "../hoverLoaders/HoverOrderLoader"
import { HoverOrderRequestLoader } from "../hoverLoaders/HoverOrderRequestLoader"
import { HoverProductLoader } from "../hoverLoaders/HoverProductLoader"
import { HoverProjectLoader } from "../hoverLoaders/HoverProjectLoader"
import { HoverPurchaseOrderLoader } from "../hoverLoaders/HoverPurchaseOrderLoader"
import { HoverQuoteLoader } from "../hoverLoaders/HoverQuoteLoader"
import { HoverUserLoader } from "../hoverLoaders/HoverUserLoader"
import { buildRoute, eRoutes } from "../routes"
import { IRecord, Models } from "../types/schema"
import { DownloadLinkButton } from "./Buttons/DownloadLink"
import { ConsolidatedOrderList } from "./ConsolidatedOrderList"
import { brandColors, colors, themeColors } from "./EOSThemeProvider"
import { ExternalLink } from "./ExternalLink"
import { useGlobalState } from "./GlobalStateProvider"
import { KeyValueList } from "./KeyValueList"
import { QuickToolTip } from "./QuickTooltip"
import { QuickToolTipWrapper } from "./QuickToolTipWrapper"
import { ISupportTicketStatus } from "./SupportTicketForm"
import { TextWithNewLines } from "./TextWithNewLines"
import { HoverSubCustomerLoader } from "../hoverLoaders/HoverSubCustomerLoader"
import { Button as NdButton, nd, Timeline } from "./NewDesign"

interface IEventsListProps {
  model: IEventModel
  id: string
  limit?: number
  viewAllLink?: string
  refetchKey?: number
  skeemFilter?: any
  itemFilter?: (item: IEvent) => boolean
  /** Show what changed on update events straight away, rather than behind "View details" */
  expandChanges?: boolean
}

export function EventList({
  model,
  id,
  limit,
  viewAllLink,
  refetchKey,
  skeemFilter,
  itemFilter,
  expandChanges,
}: IEventsListProps) {
  const events = useEventRecords({ model, id, skeemFilter, refetchKey })

  if (!events) {
    return (
      <Grid
        justifyItems="center"
        css={css`
          align-content: center;
        `}
      >
        <Spinner color="blue" />
        <br />
        {""}
        Loading Activity...
      </Grid>
    )
  } else if (events.length === 0) {
    return (
      <div>
        <P color="#999" size="small">
          No history
        </P>
      </div>
    )
  } else {
    const renderData = events.slice(0, limit || events.length)
    return (
      <div>
        <Grid gap={24}>
          {renderData
            .filter(item => (itemFilter ? itemFilter(item) : true))
            .map((event, i) => (
              <EventItem key={i} event={event} expandChanges={expandChanges} />
            ))}
        </Grid>
        {viewAllLink && limit && events.length > limit && (
          <ExternalLink
            to={viewAllLink}
            css={css`
              font-size: 12px;
              display: block;
              padding-top: 12px;
            `}
          >
            View All <Icon icon={icons.external} size={10} />
          </ExternalLink>
        )}
      </div>
    )
  }
}

/** Loads a record's events, newest first. Shared by the classic list and the new design timeline. */
export function useEventRecords({
  model,
  id,
  skeemFilter,
  refetchKey,
}: {
  model: IEventModel
  id: string
  skeemFilter?: any
  refetchKey?: number
}) {
  const [events, setEvents] = useState<IEvent[] | null>(null)

  useAsyncEffect(
    async ({ isAlive }) => {
      const events = await window.skeem.fetch2<IEvent[]>({
        model: {
          name: "events",
          attributes: [
            "type",
            "recordId",
            "details",
            "model",
            "createdAt",
            { name: "user", attributes: ["name"] },
          ],
          filter: {
            and: [{ matches: { model, recordId: id } }, skeemFilter].filter(Boolean),
          },
          sort: { by: "createdAt", direction: "desc" },
          paginate: {
            size: 999,
            page: 1,
          },
        },
      })
      if (!isAlive()) return
      setEvents(events)
    },
    [model, id, JSON.stringify(skeemFilter), refetchKey],
  )

  return events
}

/**
 * When true, event cards render as new design Timeline items (see EventTimeline) rather than
 * classic cards, so both designs show every event type the same way.
 */
export const EventNewDesignContext = React.createContext(false)

/** One event, in whichever design the surrounding EventNewDesignContext asks for. */
export function EventItem({ event, expandChanges }: { event: IEvent; expandChanges?: boolean }) {
  return (
    <React.Fragment>
      {event.type === "create" && <EventCreationCard key={event.id} event={event} />}
      {event.type === "update" && (
        <EventUpdateCard key={event.id} event={event} expandChanges={expandChanges} />
      )}
      {event.type === "createAssociation" && (
        <EventCreateAssociationCard key={event.id} event={event} />
      )}
      {event.type === "updateAssociation" && (
        <EventUpdateAssociationCard key={event.id} event={event} />
      )}
      {(event.type === "associateTo" || event.type === "unassociateTo") && (
        <EventAssociationToCard key={event.id} event={event} />
      )}
      {event.type === "notes" && <EventNotesCard key={event.id} event={event} />}
      {event.type === "userContacted" && <EventUserContactedCard key={event.id} event={event} />}
      {event.type === "ticketOpened" && (
        <EventSupportTicketOpenedCard key={event.id} event={event} />
      )}
      {event.type === "ticketActive" && (
        <EventSupportTicketAssignedCard key={event.id} event={event} />
      )}
      {event.type === "ticketClosed" && (
        <EventSupportTicketClosedCard key={event.id} event={event} />
      )}
      {event.type === "emailSent" && <EventMicrosoftEmail event={event} />}
      {event.type === "emailReceived" && <EventMicrosoftEmail event={event} />}
      {event.type === "eventUpdated" && <EventMicrosoftEvent event={event} />}
      {event.type === "eventCreated" && <EventMicrosoftEvent event={event} />}
      {event.type === "eventInvited" && <EventMicrosoftEvent event={event} />}
      {event.type === "archived" && <EventArchivedCard event={event} />}
      {event.type === "merged" && <EventMergedCard event={event} />}
      {event.type === "backOrder" && <EventBackOrderCard event={event} />}
      {event.type === "consolidation" && <EventConsolidationCard event={event} />}
      {event.type === "sageExport" && <EventSageExport event={event} />}
      {event.type === "sageUpdate" && <EventSageUpdate event={event} />}
      {event.type === "error" && <EventErrorCard event={event} />}
      {event.type === "action" && <EventActionCard event={event} />}
      {event.type === "signIn" && <EventSignIn event={event} />}
      {event.type === "creditLimitExceeded" && <CreditLimitEvent event={event} />}
      {event.type === "customerIsNewBusinessChanged" && <EventSimpleSummary event={event} />}
      {event.type === "priceBandChange" && (
        <EventPriceBandChangeCard key={event.id} event={event} />
      )}
    </React.Fragment>
  )
}

function EventSimpleSummary({ event }: { event: IEvent }) {
  return (
    <EventCardWrapper color={themeColors.blue} icon={icons.summary} event={event}>
      <P>{titleize(event.type)}</P>
      <KeyValueList
        data={Object.entries(event.details ?? {}).map(([key, value]) => ({
          label: titleize(key),
          value: JSON.stringify(value, null, 2),
        }))}
        labelWidth={120}
        inline
      />
      <Spacer size={4} />
    </EventCardWrapper>
  )
}

function CreditLimitEvent({ event }: { event: ICreditLimitEvent }) {
  const resourceForLoader = hoverLoaderForResource(event.details.resourceId)[
    event.details.resourceType as IEventModel
  ]
  return (
    <EventCardWrapper color={themeColors.alertRed} icon={icons.warning} event={event}>
      <P>Credit limit was exceeded</P>
      <P>
        {titleize(singularNames[event.details.resourceType as IEventModel] ?? "Unknown resource")}:{" "}
        {resourceForLoader}
      </P>
      <KeyValueList
        data={[
          {
            label: "Limit",
            value: {
              currency: event.details.currency,
              price: event.details.creditLimit,
              pennies: true,
            },
          },
          {
            label: "Outstanding",
            value: {
              currency: event.details.currency,
              price: event.details.outstanding,
              pennies: true,
            },
          },
          {
            label: "Amount changed",
            value: {
              currency: event.details.currency,
              price: event.details.amountChanged,
              pennies: true,
            },
          },
          {
            label: "Over limit by",
            value: {
              currency: event.details.currency,
              price:
                event.details.outstanding + event.details.amountChanged - event.details.creditLimit,
              pennies: true,
            },
          },
        ]}
      />
      <Spacer size={4} />
      <P size="small">The values above are a snapshot at the time of the event.</P>
    </EventCardWrapper>
  )
}

function EventSignIn({ event }: { event: ISignInEvent }) {
  return (
    <EventCardWrapper color={"green"} icon={icons.login} event={event}>
      <P>User logged in</P>
    </EventCardWrapper>
  )
}

type IIcon = (typeof icons)[keyof typeof icons]
interface IActionEventTypeData {
  icon: IIcon
  color: string
}
interface IActionEventTypes {
  "Customer Application Archived": IActionEventTypeData
  "Delivery note emailed": IActionEventTypeData
  "Fulfillment generated": IActionEventTypeData
  "Manual processing removed": IActionEventTypeData
  "Manually processed": IActionEventTypeData
  "Status overridden": IActionEventTypeData
  "Internal Export": IActionEventTypeData
  Error: IActionEventTypeData
  "Linked Export": IActionEventTypeData
  Cancelled: IActionEventTypeData
  "Status Update": IActionEventTypeData
  "Auto Export Failed": IActionEventTypeData
  "Auto Export Success": IActionEventTypeData
  "Picking Ticket Downloaded": IActionEventTypeData
  "Order dispatched": IActionEventTypeData
  "Invoice Emailed": IActionEventTypeData
  "Purchase Order Back Order": IActionEventTypeData
  "Converted to shared price band": IActionEventTypeData
  "Duplicated price band": IActionEventTypeData
  "Allocations updated": IActionEventTypeData
  "Order unallocated": IActionEventTypeData
  "Marked as complete": IActionEventTypeData
}
type IActionEventType = keyof IActionEventTypes
function EventActionCard({ event }: { event: IActionEvent }) {
  const { currentUser } = useGlobalState()
  const iconMap: IActionEventTypes = {
    "Customer Application Archived": { icon: icons.archive, color: themeColors.orange },
    "Delivery note emailed": { icon: icons.sentEmail, color: themeColors.green },
    "Fulfillment generated": { icon: icons.circleCheck, color: themeColors.green },
    "Manual processing removed": { icon: icons.circleMinus, color: themeColors.red },
    "Manually processed": { icon: icons.circleCheck, color: themeColors.green },
    "Status overridden": { icon: icons.copy, color: themeColors.orange },
    "Auto Export Failed": { icon: icons.circleCross, color: themeColors.red },
    "Auto Export Success": { icon: icons.circleCheck, color: themeColors.green },
    "Status Update": {
      icon: event.details.data?.icon ?? icons.circleCheck,
      color: event.details.data?.color ?? themeColors.green,
    },
    "Internal Export": { icon: icons.circleCheck, color: themeColors.green },
    "Invoice Emailed": { icon: icons.circleCheck, color: themeColors.green },
    "Linked Export": { icon: icons.circleCheck, color: themeColors.green },
    Cancelled: { icon: icons.circleCross, color: themeColors.red },
    Error: { icon: icons.alert, color: themeColors.red },
    "Picking Ticket Downloaded": { icon: icons.download, color: themeColors.green },
    "Order dispatched": { icon: icons.circleCheck, color: themeColors.green },
    "Purchase Order Back Order": { icon: icons.purchaseOrders, color: themeColors.blue },
    "Converted to shared price band": { icon: icons.sharedPriceBands, color: themeColors.green },
    "Duplicated price band": { icon: icons.duplicate, color: themeColors.blue },
    "Allocations updated": { icon: icons.warehouseFull, color: themeColors.blue },
    "Order unallocated": { icon: icons.warehouseFull, color: themeColors.orange },
    "Marked as complete": { icon: icons.check, color: brandColors.darkMint },
  }
  const icon = iconMap[event.details.name as keyof typeof iconMap] || {
    icon: icons.circleCheck,
    color: themeColors.blue,
  }

  function omitData(data: Record<string, any>, keys: string[]) {
    const clone = JSON.parse(JSON.stringify(data))
    keys.forEach(key => delete clone[key])
    return clone
  }

  const displayStatus = event.details.data?.friendlyStatus || event.details.data?.status
  return (
    <EventCardWrapper color={icon.color} icon={icon.icon as any} event={event}>
      <P>
        {event.details.name}
        {displayStatus && (
          <span>
            {" "}
            to <b>{displayStatus}</b>
          </span>
        )}
      </P>
      {event.details.body && (
        <TextBlock size="small">
          <TextWithNewLines text={event.details.body} />
        </TextBlock>
      )}
      {currentUser.isSuperAdmin &&
        event.details.data &&
        (typeof event.details.data === "object" ? (
          <RawData src={omitData(event.details.data, ["icon", "color"])} />
        ) : (
          JSON.stringify(omitData(event.details.data, ["icon", "color"]), null, 2)
        ))}
    </EventCardWrapper>
  )
}

function EventErrorCard({ event }: { event: IErrorEvent }) {
  const { currentUser } = useGlobalState()
  return (
    <EventCardWrapper color={themeColors.red} icon={icons.alert} event={event}>
      An error occurred for this {titleize(singularize(event.model ?? ""))}.
      <P size="small" color="red">
        {event.details.message}
      </P>
      {event.details.reason && (
        <P size="small" color="red">
          {event.details.reason}
        </P>
      )}
      {currentUser.isSuperAdmin &&
        event.details.details &&
        (typeof event.details.details === "object" ? (
          <RawData src={event.details.details} />
        ) : (
          JSON.stringify(event.details.details, null, 2)
        ))}
    </EventCardWrapper>
  )
}

function EventCreationCard({ event }: { event: IEvent }) {
  const { currentUser } = useGlobalState()
  const canSeeRaw =
    currentUser &&
    (currentUser.isSuperAdmin ||
      ["alistair.richards@cd2solutions.co.uk"].includes(currentUser.email))
  const rawData = event.details
  // Some create events record a readable summary of what the record was created with
  const { source, summary } = (rawData ?? {}) as {
    source?: string
    summary?: { label: string; value: React.ReactNode }[]
  }
  return (
    <EventCardWrapper color={themeColors.green} icon={icons.new} event={event}>
      {titleize(singularize(event.model ?? ""))} created
      {source && <span> via {source}</span>}
      {Array.isArray(summary) && summary.length > 0 && (
        <React.Fragment>
          <Spacer size={4} />
          <KeyValueList data={summary} labelWidth={120} inline />
        </React.Fragment>
      )}
      {canSeeRaw && !summary && (
        <div>
          <Spacer size={8} />
          <RawData src={rawData ?? {}} />
        </div>
      )}
    </EventCardWrapper>
  )
}

function EventSupportTicketOpenedCard({ event }: { event: IEvent }) {
  return (
    <EventCardWrapper color={themeColors.green} icon={icons.new} event={event}>
      {titleize(singularize(event.model ?? ""))} opened
    </EventCardWrapper>
  )
}

function EventSupportTicketAssignedCard({ event }: { event: IEvent }) {
  return (
    <EventCardWrapper color={brandColors.darkMint} icon={icons.check} event={event}>
      {titleize(singularize(event.model ?? ""))} started
    </EventCardWrapper>
  )
}

function EventSupportTicketClosedCard({ event }: { event: ICloseTicketEvent }) {
  return (
    <EventCardWrapper color={brandColors.blaze} icon={icons.archive} event={event}>
      {titleize(singularize(event.model ?? ""))} closed
      {event.details?.message && (
        <P
          css={css`
            white-space: pre-wrap;
          `}
        >
          {event.details.message}
        </P>
      )}
    </EventCardWrapper>
  )
}

function EventUpdateCard({ event, expandChanges }: { event: IEvent; expandChanges?: boolean }) {
  const newDesign = useContext(EventNewDesignContext)
  const eventDetails = event.details as IChange[] | undefined
  return (
    <EventCardWrapper color={brandColors.darkMint} icon={icons.edit} event={event}>
      Updated this {titleize(singularize(event.model ?? ""))}
      {expandChanges && eventDetails && newDesign ? (
        <ChangeTable details={eventDetails} />
      ) : expandChanges && eventDetails ? (
        <React.Fragment>
          <Spacer size={4} />
          <KeyValueList data={eventDetails.map(item => changeItem({ item }))} labelWidth={120} />
        </React.Fragment>
      ) : (
        <ChangeDetails details={eventDetails} />
      )}
    </EventCardWrapper>
  )
}

function PriceBandNameLink({ id }: { id: string }) {
  const skeem = window.skeem
  const [name, setName] = useState<string | null>(null)
  useAsyncEffect(async () => {
    const priceBand = await skeem.fetch2<IRecord<"price_bands", "name">>({
      first: { name: "price_bands", attributes: ["name"], matches: { id } },
    })
    setName(priceBand?.name ?? null)
  }, [id])
  return (
    <ExternalLink to={buildRoute(eRoutes["/stock/price-bands/:id"], { id })}>
      {name ?? id}
    </ExternalLink>
  )
}

function priceBandChangeDetailValue(key: string, value: unknown): React.ReactNode {
  if (key === "copyFromCustomerId" && typeof value === "string") {
    return <HoverCustomerLoader id={value} />
  }
  if (
    (key === "copyFromPriceBandId" || key === "copyFromCustomerPriceBandId") &&
    typeof value === "string"
  ) {
    return <PriceBandNameLink id={value} />
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

function EventPriceBandChangeCard({ event }: { event: IPriceBandChangeEvent }) {
  const { action, itemMutations, details } = event.details
  const detailEntries = details
    ? Object.entries(details).filter(([, value]) => value != null && value !== "")
    : []

  return (
    <EventCardWrapper color={brandColors.darkMint} icon={icons.edit} event={event}>
      <P>
        {action} — {pluralize(itemMutations.length, "item")} changed
      </P>
      {detailEntries.length > 0 && (
        <React.Fragment>
          <KeyValueList
            data={detailEntries.map(([key, value]) => ({
              label: titleize(key),
              value: priceBandChangeDetailValue(key, value),
            }))}
            labelWidth={160}
            inline
          />
          <Spacer size={4} />
        </React.Fragment>
      )}
      <ChangeDetails details={itemMutations} />
    </EventCardWrapper>
  )
}

function ChangeDetails({ details }: { details?: IChange[] }) {
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const newDesign = useContext(EventNewDesignContext)
  if (!details) {
    return null
  }
  if (newDesign) {
    return (
      <div>
        <NdButton
          variant="plain"
          label={
            detailsExpanded
              ? "Hide changes"
              : `Show ${pluralize(details.length, "change", "changes")}`
          }
          onClick={() => setDetailsExpanded(!detailsExpanded)}
        />
        {detailsExpanded && <ChangeTable details={details} />}
      </div>
    )
  }
  return (
    <div>
      {!detailsExpanded && (
        <React.Fragment>
          <Button
            extendTheme={{ fontSize: 12, padding: 6 }}
            buttonStyle="outline"
            onClick={() => setDetailsExpanded(true)}
          >
            View details
          </Button>
          <Spacer size={8} />
        </React.Fragment>
      )}
      {detailsExpanded && <KeyValueList data={details.map(item => changeItem({ item }))} />}
      {detailsExpanded && (
        <React.Fragment>
          <Spacer size={8} />
          <Button
            extendTheme={{ fontSize: 12, padding: 6 }}
            buttonStyle="outline"
            onClick={() => setDetailsExpanded(false)}
          >
            Hide details
          </Button>
        </React.Fragment>
      )}
    </div>
  )
}

/** Raw event data for super admins: open in the classic list, behind a toggle in the timeline. */
function RawData({ src }: { src: object }) {
  const newDesign = useContext(EventNewDesignContext)
  const [open, setOpen] = useState(false)
  if (!newDesign) {
    return <ReactJson src={src} />
  }
  return (
    <div>
      <NdButton
        variant="plain"
        label={open ? "Hide raw data" : "Show raw data (super admin only)"}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <div
          css={css`
            margin-top: ${nd.space.xs}px;
            overflow-x: auto;
          `}
        >
          <ReactJson src={src} collapsed={1} />
        </div>
      )}
    </div>
  )
}

/** New design: what changed as a small Field / From / To table, nested changes indented. */
function ChangeTable({ details }: { details: IChange[] }) {
  const rows: { key: string; depth: number; item: IChange }[] = []
  function addRows(items: IChange[], depth: number, prefix: string) {
    items.forEach((item, i) => {
      const key = `${prefix}${i}`
      rows.push({ key, depth, item })
      const nested = (item as { data?: unknown }).data
      if (Array.isArray(nested)) {
        addRows(nested as IChange[], depth + 1, `${key}.`)
      }
    })
  }
  addRows(details, 0, "")
  const cellCss = css`
    padding: 6px ${nd.space.md}px 6px 0;
    border-top: 1px solid ${nd.color.border};
    vertical-align: top;
    text-align: left;
    overflow-wrap: anywhere;
  `
  return (
    <div
      css={css`
        margin-top: ${nd.space.sm}px;
        overflow-x: auto;
      `}
    >
      <table
        css={css`
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          line-height: 18px;
          color: ${nd.color.text};
          th {
            color: ${nd.color.textSubdued};
            font-weight: ${nd.fontWeight.medium};
          }
        `}
      >
        <thead>
          <tr>
            <th css={cellCss}>Field</th>
            <th css={cellCss}>From</th>
            <th css={cellCss}>To</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, depth, item }) => {
            const hasNested = Array.isArray((item as { data?: unknown }).data)
            const action =
              item.type === "add" ? "added" : item.type === "remove" ? "removed" : undefined
            return (
              <tr key={key}>
                <td
                  css={css`
                    ${cellCss};
                    padding-left: ${depth * nd.space.lg}px;
                    font-weight: ${hasNested ? nd.fontWeight.semibold : "inherit"};
                  `}
                >
                  {item.name?.trim()}
                  {action && (
                    <span
                      css={css`
                        color: ${nd.color.textSubdued};
                      `}
                    >
                      {" "}
                      ({action})
                    </span>
                  )}
                </td>
                <td
                  css={css`
                    ${cellCss};
                    color: ${nd.color.textSubdued};
                  `}
                >
                  {hasNested ? null : getTextValue("previous", item)}
                </td>
                <td css={cellCss}>{hasNested ? null : getTextValue("current", item)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ContactLoader({ id }: { id: string }) {
  const skeem = window.skeem
  const [contact, setContact] = useState<IRecord<"contacts", "name"> | null>(null)
  useAsyncEffect(async () => {
    const contact = await skeem.fetch2<IRecord<"contacts", "name">>({
      first: { name: "contacts", attributes: ["name"], matches: { id: id } },
    })
    if (contact?.id !== id) {
      setContact(null)
      return
    }
    setContact(contact)
  }, [id])

  return <span>{contact?.name ?? "Unknown contact"}</span>
}

function getTextValue(value: "previous" | "current", item: IChange) {
  const itemValue = item[value]
  if (!itemValue) {
    return "No Value"
  }
  if ("display" in item && item.display?.model) {
    const loader = hoverLoaderForResource(itemValue)[item.display.model]
    return loader
  }

  if (typeof itemValue === "string") {
    return itemValue
  }
  return JSON.stringify(itemValue) ?? "No Value"
}
function changeItem({ item }: { item: IChange }) {
  function TextValue({ change }: { change: IChange }) {
    const prevValue = getTextValue("previous", change)
    const nextValue = getTextValue("current", change)
    return (
      <P size="small">
        {prevValue} {"=>"} {nextValue}
      </P>
    )
  }
  const type = "type" in item ? item.type : null
  return {
    label: doo(() => {
      if (type === "add") {
        return `${item.name?.trim()} Created`
      }
      if (type === "remove") {
        return `${item.name?.trim()} Deleted`
      }
      if (type === "update") {
        return `${item.name?.trim()} Updated`
      }
      return item.name?.trim()
    }),
    value: doo(() => {
      if ("data" in item && Array.isArray(item.data)) {
        return (
          <div
            css={css`
              padding: 8px 0 8px 12px;
            `}
          >
            <KeyValueList data={item.data?.map(item => changeItem({ item })) ?? []} />
          </div>
        )
      }
      return <TextValue change={item} />
    }),
  }
}

function EventArchivedCard({ event }: { event: IArchivedEvent }) {
  return (
    <EventCardWrapper color={themeColors.orange} icon={icons.archive} event={event}>
      {titleize(singularize(event.model))} was {event.details.archived ? "archived" : "restored"}
      {event.details.reason && <P size="small">Reason: {event.details.reason}</P>}
    </EventCardWrapper>
  )
}

function EventMergedCard({ event }: { event: IMergedEvent }) {
  return (
    <EventCardWrapper color={themeColors.orange} icon={icons.archive} event={event}>
      {titleize(singularize(event.model))} was merged with {event.details.records.length} other
      records.
      <Spacer size={4} />
      <Grid gap={4}>
        {event.details.records.map(record => {
          const hoverLoader = hoverLoaderForResource(record.id)[event.model as IEventModel]
          return (
            <QuickToolTipWrapper
              key={record.id}
              showToolTip={!!record.details}
              content={
                <KeyValueList
                  data={Object.entries(record.details ?? {}).map(([key, value]) => ({
                    label: titleize(key),
                    value: JSON.stringify(value, null, 2),
                  }))}
                />
              }
            >
              <div>
                {hoverLoader && !record.details ? (
                  hoverLoader
                ) : (
                  <P size="small" blockSpacing={0}>
                    {record.name}
                  </P>
                )}
              </div>
            </QuickToolTipWrapper>
          )
        })}
      </Grid>
    </EventCardWrapper>
  )
}

function EventBackOrderCard({ event }: { event: IBackOrderEvent }) {
  const targetHoverLoader = hoverLoaderForResource(event.details.recordId)["orders"]
  return (
    <EventCardWrapper color={brandColors.darkMint} icon={icons.refresh} event={event}>
      A back order was created: {targetHoverLoader}.
    </EventCardWrapper>
  )
}

function EventConsolidationCard({ event }: { event: IConsolidationEvent }) {
  const comp =
    event.model === "orders" ? (
      <ConsolidatedOrderList consolidatedOrderKey={event.details.consolidationKey} />
    ) : null
  return (
    <EventCardWrapper color={brandColors.darkMint} icon={icons.refresh} event={event}>
      {titleize(singularize(event.model))} was consolidated.
      {comp && <Spacer size={4} />}
      {comp}
    </EventCardWrapper>
  )
}

/** Who can see the raw data sent to Sage on the export and update cards */
function canSeeRawSageData(currentUser: { id?: string | null; isSuperAdmin?: boolean | null }) {
  return (
    !!currentUser.id &&
    (!!currentUser.isSuperAdmin ||
      ["29ae6bb6-8cb4-46d1-bfd4-42bda001d523"].includes(currentUser.id))
  )
}

function EventSageExport({ event }: { event: ISageExport }) {
  const { currentUser } = useGlobalState()
  const rawData = event.details?.data
  const data =
    typeof rawData == "string"
      ? (() => {
          try {
            return JSON.parse(rawData)
          } catch (e) {
            return { error: "Error parsing raw data", raw: rawData }
          }
        })()
      : rawData
  return (
    <EventCardWrapper color={colors.sage} icon={icons.sageImports} event={event}>
      {titleize(singularize(event.model))} was exported to Sage
      {event.details?.exportedBy ? <span> by {event.details.exportedBy}</span> : "."}
      {canSeeRawSageData(currentUser) && event.details.data && (
        <div>
          <Spacer size={8} />
          <RawData src={data} />
        </div>
      )}
    </EventCardWrapper>
  )
}

/** A purchase order's changes being sent to the order in Sage, with what was sent */
function EventSageUpdate({ event }: { event: ISageUpdate }) {
  const { currentUser } = useGlobalState()
  const changes = event.details?.changes ?? []
  return (
    <EventCardWrapper color={colors.sage} icon={icons.sageImports} event={event}>
      {titleize(singularize(event.model))} was updated in Sage.
      {changes.length > 0 && (
        <ul
          css={css`
            margin: 4px 0 0;
            padding-left: 20px;
          `}
        >
          {changes.map((change, index) => (
            <li key={index}>{change.description}</li>
          ))}
        </ul>
      )}
      {canSeeRawSageData(currentUser) && event.details?.data && (
        <div>
          <Spacer size={8} />
          <RawData src={event.details.data} />
        </div>
      )}
    </EventCardWrapper>
  )
}

function UpdateAssocChangeList({ changes }: { changes?: Record<string, { from: any; to: any }> }) {
  if (!changes) {
    return null
  }
  const keys = Object.keys(changes)
  return (
    <div>
      <Spacer size={4} />
      <KeyValueList
        data={keys.map(key => ({
          label: titleize(key),
          value: (
            <div key={key}>
              <P size="small" blockSpacing={0}>
                from: {changes[key].from}
              </P>
              <P size="small" blockSpacing={0}>
                to: {changes[key].to}
              </P>
            </div>
          ),
        }))}
        labelWidth={60}
        inline
      />
      <Spacer size={4} />
    </div>
  )
}
function EventUpdateAssociationCard({ event }: { event: IUpdateAssociationEvent }) {
  return (
    <EventCardWrapper color={themeColors.orange} icon={icons.new} event={event}>
      Updated {singularNames[event.details.association]}:{" "}
      {hoverLoaderForResource(event.details.recordId)[event.details.association]}
      <UpdateAssocChangeList changes={event.details.changes} />
    </EventCardWrapper>
  )
}

function EventCreateAssociationCard({ event }: { event: ICreateAssociationEvent }) {
  return (
    <EventCardWrapper color={themeColors.orange} icon={icons.new} event={event}>
      New {singularNames[event.details.association]} added:{" "}
      {hoverLoaderForResource(event.details.recordId)[event.details.association]}
    </EventCardWrapper>
  )
}

function EventAssociationToCard({ event }: { event: IAssociationToEvent | IUnassociationToEvent }) {
  const isAssociate = event.type === "associateTo"
  const skeem = window.skeem
  const [resourceName, setResourceName] = useState("")
  const resourceLoader =
    hoverLoaderForResource(event.details.recordId)[event.details.association] || resourceName

  useAsyncEffect(async () => {
    if (event.details.nameAttr && !resourceLoader) {
      try {
        const record = await skeem.fetch2<{ [key: string]: string }>({
          first: {
            name: event.details.association,
            attributes: [event.details.nameAttr],
            filter: { matches: { id: event.details.recordId } },
          },
        })
        const name = record?.[event.details.nameAttr]
        setResourceName(name || "Unknown")
      } catch (e) {
        setResourceName("Unknown")
        console.error(e)
        console.error(event)
      }
    }
  }, [event.details.nameAttr])

  return (
    <EventCardWrapper
      color={isAssociate ? themeColors.orange : themeColors.red}
      icon={isAssociate ? icons.new : icons.circleMinus}
      event={event}
    >
      {titleize(singularNames[event.details.association] ?? "")} {resourceLoader}{" "}
      {isAssociate ? "assigned to" : "unassigned from"}{" "}
      {titleize(singularNames[event.model as keyof typeof singularNames] ?? "")}
    </EventCardWrapper>
  )
}

function EventNotesCard({ event }: { event: IAddNotesToEvent }) {
  const skeem = window.skeem
  const fileboy = useFileboy()
  const { message, communication_type, contactId, contact_details, files } = event.details

  const [contact, setContact] = useState<IRecord<"contacts", "name" | "email"> | null>(null)
  useAsyncEffect(async () => {
    if (!contactId) {
      setContact(null)
      return
    }
    const contact = await skeem.fetch2<IRecord<"contacts", "name" | "email">>({
      first: { name: "contacts", attributes: ["name", "email"], matches: { id: contactId } },
    })
    setContact(contact)
  }, [contactId])

  const icon = (() => {
    if (communication_type === "phone") {
      return icons.phone
    }
    if (communication_type === "other") {
      return icons.phone
    }
    if (communication_type === "email") {
      return icons.mail
    }
    return icons.text
  })()

  return (
    <EventCardWrapper color={themeColors.orange} icon={icon} event={event}>
      <P>{communication_type ? titleize(communication_type) : "Notes"}:</P>
      <P
        size="small"
        css={css`
          white-space: pre-wrap;
        `}
      >
        {message}
      </P>
      {contact && (
        <P size="small">
          Contact: {contact.name} ({contact.email})
        </P>
      )}
      {contact_details && <P size="small">Contacted via: {contact_details}</P>}
      {files && files.length > 0 ? (
        <div>
          <QuickToolTip
            content={
              <Grid gap={8}>
                <P color="white">Download attachments</P>
                {files.map(file => (
                  <DownloadLinkButton
                    key={file.fileboyId}
                    to={fileboy.buildFileUrl(file.fileboyId, {
                      download: true,
                      filename: file.fileName,
                    })}
                  >
                    {file.fileName}
                  </DownloadLinkButton>
                ))}
              </Grid>
            }
            interactive
          >
            <P
              css={css`
                border-bottom: 1px dotted black;
                width: max-content;
                cursor: pointer;
              `}
              size="small"
              weight={600}
            >
              {pluralize(files.length, "attachment", "attachments")}
            </P>
          </QuickToolTip>
        </div>
      ) : null}
    </EventCardWrapper>
  )
}

function EventUserContactedCard({ event }: { event: IAddUserContactedToEvent }) {
  return (
    <EventCardWrapper color={themeColors.orange} icon={icons.addressBook} event={event}>
      <P>User Contacted</P>
      <KeyValueList
        data={[
          { label: "Name", value: event.details.name },
          { label: titleize(event.details.type), value: event.details.contact },
          { label: "Notes", value: event.details.message, returnNullOnEmpty: true },
        ]}
        labelWidth={60}
        inline
      />
      <Spacer size={8} />
    </EventCardWrapper>
  )
}

type IMsEmail = IRecord<"microsoftEmails", "subject"> & {
  sender: I365Recipient
  recipients: I365Recipient[]
}

function EventMicrosoftEmail({ event }: { event: IMicrosoftEmailEvent }) {
  const skeem = window.skeem
  const [email, setEmail] = useState<IMsEmail | null>(null)

  useAsyncEffect(async () => {
    const email = await skeem.fetch2<IMsEmail>({
      first: {
        name: "microsoftEmails",
        attributes: ["sender", "recipients", "subject"],
        filter: { matches: { id: event.details.id } },
      },
    })
    setEmail(email)
  }, [event.details.id])

  return (
    <EventCardWrapper
      color={event.type === "emailReceived" ? brandColors.darkMint : themeColors.green}
      icon={icons.mail}
      event={event}
    >
      <P>{event.type === "emailReceived" ? "Email Received" : "Email Sent"}</P>
      <H4>{email?.subject}</H4>
      {email && (
        <TextBlock size="small">
          {event.type === "emailReceived" ? (
            <Grid columns={["max-content", "auto"]} gap={8}>
              <P weight={600}>From:</P>
              <P>
                {email.sender.name} ({email.sender.address})
              </P>
            </Grid>
          ) : (
            <Grid columns={["max-content", "auto"]} gap={8}>
              <P weight={600}>To:</P>
              <div>
                {email.recipients.map(r => (
                  <P key={r.address}>
                    {r.name} ({r.address})
                  </P>
                ))}
              </div>
            </Grid>
          )}
        </TextBlock>
      )}
    </EventCardWrapper>
  )
}

type IMsEvent = IRecord<"microsoftEvents", "subject"> & {
  organizer: I365Recipient
  attendees: I365Attendee[]
}

function EventMicrosoftEvent({ event }: { event: IMicrosoftEventEvent }) {
  const skeem = window.skeem
  const [msEvent, setMsEvent] = useState<IMsEvent | null>(null)

  const allowedUpdateKeys = ["subject", "body", "locations", "attendees"]
  const filteredUpdated =
    event.details.updatedKeys?.filter(key => allowedUpdateKeys.includes(key)) ?? []

  useAsyncEffect(async () => {
    const msEvent = await skeem.fetch2<IMsEvent>({
      first: {
        name: "microsoftEvents",
        attributes: ["organizer", "attendees", "subject"],
        filter: { matches: { id: event.details.id } },
      },
    })
    setMsEvent(msEvent)
  }, [event.details.id])

  return (
    <EventCardWrapper
      color={
        event.type === "eventInvited" || event.type === "eventUpdated"
          ? brandColors.darkMint
          : themeColors.green
      }
      icon={icons.calendar}
      event={event}
    >
      <P>
        {event.type === "eventInvited"
          ? "Invited to Event"
          : event.type === "eventUpdated"
          ? "Event Updated"
          : "Event Created"}
      </P>
      <H4>{msEvent?.subject}</H4>
      {msEvent && (
        <TextBlock size="small">
          <Grid columns={["max-content", "auto"]} gap={8}>
            <P weight={600}>Organizer:</P>
            <P>
              {msEvent.organizer.name} ({msEvent.organizer.address})
            </P>
          </Grid>

          <Grid columns={["max-content", "auto"]} gap={8}>
            <P weight={600}>Attendees:</P>
            <div>
              {msEvent.attendees.length === 0 ? (
                <P>No attendees listed</P>
              ) : (
                msEvent.attendees.map(r => (
                  <P key={r.emailAddress.address}>
                    {r.emailAddress.name} ({r.emailAddress.address})
                  </P>
                ))
              )}
            </div>
          </Grid>
          {filteredUpdated.length > 0 && (
            <Grid columns={["max-content", "auto"]} gap={8}>
              <P weight={600}>Updated:</P>
              <div>
                {filteredUpdated
                  .filter(key => allowedUpdateKeys.includes(key))
                  .map(titleize)
                  .join(", ")}
              </div>
            </Grid>
          )}
        </TextBlock>
      )}
    </EventCardWrapper>
  )
}

function EventCardWrapper({
  icon,
  event,
  children,
  color,
}: {
  icon?: IconDefinition
  event: IRecord<"events", "createdAt"> & {
    user: IRecord<"skeem_users", "name">
  }
  children: React.ReactNode
  color?: string
}) {
  const newDesign = useContext(EventNewDesignContext)
  if (newDesign) {
    return (
      <Timeline.Item
        icon={icon}
        iconColor={color}
        title={children}
        time={event.createdAt && format(new Date(event.createdAt), "HH:mm")}
        meta={event.user && <HoverUserLoader id={event.user.id} />}
      />
    )
  }
  return (
    <Grid
      columns={[30, "auto"]}
      gap={6}
      css={css`
        position: relative;

        :not(:last-child):after {
          content: "";
          position: absolute;
          left: 12px;
          top: 34px;
          height: calc(100% - 18px);
          border-left: 2px solid #dfdfdf;
        }
      `}
    >
      <div
        css={css`
          width: 26px;
          height: 26px;
          border-radius: 4px;
          background-color: ${color ?? "#dfdfdf"};
          line-height: 26px;
          color: white;
          text-align: center;
        `}
      >
        {icon && <Icon icon={icon} />}
      </div>
      <div
        css={css`
          font-size: 13px;
        `}
      >
        {children}
        <Spacer size={3} />
        <div
          css={css`
            font-size: 12px;

            a {
              color: #0e2182;
            }
          `}
        >
          {event.createdAt && (
            <span
              css={css`
                color: grey;
              `}
            >
              {format(new Date(event.createdAt), "LLL d, yyyy 'at' h:mm aaaaa'm'")}
            </span>
          )}
          {event.user && (
            <React.Fragment>
              {" by "}
              <b>
                <HoverUserLoader id={event.user.id} />
              </b>
            </React.Fragment>
          )}
        </div>
      </div>
    </Grid>
  )
}

export interface IChange {
  name: string
  type: "add" | "update" | "remove"
  display?: { type: "hoverLoader"; model: IEventModel }
  previous?: any
  current?: any
  data?: IChange[]
}

export type IPriceBandItemChange = IChange & { product_id?: string }

export function useEvents() {
  const skeem = window.skeem

  async function createRaw<T extends IEventType>(
    type: T,
    model: IEventModel,
    recordId: string,
    details?: IEventDetailsFor<T>,
  ) {
    await skeem.mutate({
      events: {
        create: { type, model, recordId, details, user: { set: { session: true } } },
      },
    })
  }

  function signIn(userId: string) {
    return createRaw("signIn", "skeem_users", userId)
  }

  function createAction(
    model: IEventModel,
    recordId: string,
    data: { name: IActionEventType; body?: string; data?: Record<string, any> },
  ) {
    return createRaw("action", model, recordId, data)
  }

  function createRecord(model: IEventModel, recordId: string, rawData?: any) {
    return createRaw("create", model, recordId, rawData)
  }

  function updateRecord(model: IEventModel, recordId: string, details?: IChange[]) {
    return createRaw("update", model, recordId, details)
  }

  function sageExport(model: IEventModel, recordId: string) {
    return createRaw("sageExport", model, recordId)
  }

  function archivedRecord(
    model: IEventModel,
    recordId: string,
    details: { archived: boolean; reason?: string },
  ) {
    return createRaw("archived", model, recordId, details)
  }

  function mergeRecords(model: IEventModel, recordId: string, details: IMergedEvent["details"]) {
    return createRaw("merged", model, recordId, details)
  }

  function backOrder(model: IEventModel, recordId: string, details: IBackOrderEvent["details"]) {
    return createRaw("backOrder", model, recordId, details)
  }

  function consolidation(
    model: IEventModel,
    recordId: string,
    details: IConsolidationEvent["details"],
  ) {
    return createRaw("consolidation", model, recordId, details)
  }

  function errorEvent(model: IEventModel, recordId: string, details: IErrorEvent["details"]) {
    return createRaw("error", model, recordId, details)
  }

  function createAssociatedRecord(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"createAssociation">,
  ) {
    return createRaw("createAssociation", model, recordId, details)
  }

  function updateAssociatedRecord(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"updateAssociation">,
  ) {
    return createRaw("updateAssociation", model, recordId, details)
  }

  function associatedToRecord(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"associateTo">,
  ) {
    return createRaw("associateTo", model, recordId, details)
  }

  async function associateWithInverse(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"associateTo">,
    inverseNameAttr?: string,
  ) {
    await createRaw("associateTo", model, recordId, details)
    return createRaw("associateTo", details.association, details.recordId, {
      association: model,
      recordId,
      nameAttr: inverseNameAttr || details.nameAttr,
    })
  }

  function unassociatedToRecord(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"unassociateTo">,
  ) {
    return createRaw("unassociateTo", model, recordId, details)
  }

  async function unassociatedWithInverse(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"unassociateTo">,
    inverseNameAttr?: string,
  ) {
    await createRaw("unassociateTo", details.association, details.recordId, {
      association: model,
      recordId,
      nameAttr: inverseNameAttr || details.nameAttr,
    })
    return createRaw("unassociateTo", model, recordId, details)
  }

  function addNotes(model: IEventModel, recordId: string, details: IEventDetailsFor<"notes">) {
    return createRaw("notes", model, recordId, details)
  }

  function addUserContacted(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"userContacted">,
  ) {
    return createRaw("userContacted", model, recordId, details)
  }

  function ticketStatusUpdated(
    model: IEventModel,
    recordId: string,
    status: ISupportTicketStatus,
    details?: { message?: string },
  ) {
    if (status === "open") {
      return createRaw("ticketOpened", model, recordId)
    }
    if (status === "active") {
      return createRaw("ticketActive", model, recordId)
    }
    if (status === "closed") {
      return createRaw("ticketClosed", model, recordId, details)
    }
  }

  function statusUpdate(
    model: IEventModel,
    recordId: string,
    status: string,
    details?: {
      icon?: (typeof icons)[keyof typeof icons]
      color?: string
      previousStatus?: string
      friendlyStatus?: string
    },
  ) {
    return createAction(model, recordId, { name: "Status Update", data: { status, ...details } })
  }

  function creditLimitExceeded(customerId: string, data: IEventDetailsFor<"creditLimitExceeded">) {
    return createRaw("creditLimitExceeded", "customers", customerId, data)
  }

  function reportAction(
    action: "view" | "download" | "load" | "regenerate",
    reportType: "custom" | "standard",
    idOrName: string,
    data?: Record<string, any>,
  ) {
    return createRaw("reportAction", "reports", idOrName, { action, reportType, data })
  }

  function customerIsNewBusinessChanged(customerId: string, newState: boolean) {
    return createRaw("customerIsNewBusinessChanged", "customers", customerId, {
      is_new_business: newState,
    })
  }

  function priceBandChange(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"priceBandChange">,
  ) {
    return createRaw("priceBandChange", model, recordId, details)
  }

  return {
    createRecord,
    updateRecord,
    createAssociatedRecord,
    updateAssociatedRecord,
    associatedToRecord,
    unassociatedToRecord,
    associateWithInverse,
    unassociatedWithInverse,
    addNotes,
    addUserContacted,
    ticketStatusUpdated,
    archivedRecord,
    mergeRecords,
    backOrder,
    consolidation,
    sageExport,
    errorEvent,
    createAction,
    signIn,
    statusUpdate,
    creditLimitExceeded,
    reportAction,
    customerIsNewBusinessChanged,
    priceBandChange,
  }
}

export type IEventType =
  | "create"
  | "update"
  | "createAssociation"
  | "updateAssociation"
  | "associateTo"
  | "unassociateTo"
  | "notes"
  | "userContacted"
  | "ticketOpened"
  | "ticketActive"
  | "ticketClosed"
  | "emailSent"
  | "emailReceived"
  | "eventCreated"
  | "eventInvited"
  | "eventUpdated"
  | "archived"
  | "merged"
  | "backOrder"
  | "consolidation"
  | "sageExport"
  | "sageUpdate"
  | "error"
  | "action"
  | "signIn"
  | "creditLimitExceeded"
  | "reportAction"
  | "customerIsNewBusinessChanged"
  | "priceBandChange"
export type IEventModel =
  | "customers"
  | "quotes"
  | "orders"
  | "support_tickets"
  | "skeem_users"
  | "support_ticket_groups"
  | "contacts"
  | "purchase_orders"
  | "orderRequests"
  | "branches"
  | "leads"
  | "projects"
  | "deliveries"
  | "rmas"
  | "products"
  | "price_bands"
  | "universal_price_bands"
  | "sub_customers"
  | "reports"
  | "stock_transfers"

export interface IEventDetails extends Record<IEventType, unknown> {
  signIn: {}
  createAssociation: { association: IEventModel; recordId: string }
  updateAssociation: {
    association: IEventModel
    recordId: string
    changes?: Record<string, { from: any; to: any }>
  }
  associateTo: {
    association: IEventModel
    recordId: string
    nameAttr?: string
  }
  unassociateTo: {
    association: IEventModel | string
    recordId: string
    nameAttr?: string
  }
  notes: {
    message: string
    contactId?: string
    communication_type?: "notes" | "phone" | "email" | string
    contact_details?: string
    files?: { fileName: string; fileboyId: string }[]
  }
  userContacted: { message?: string; type: "phone" | "email"; name: string; contact: string }
  ticketClosed: { message?: string }
  emailReceived: { id: string }
  emailSent: { id: string }
  eventCreated: { id: string }
  eventInvited: { id: string }
  eventUpdated: { id: string }
  archived: { archived: boolean; reason?: string }
  merged: { records: { id: string; name: string; details?: Record<string, string>; to?: string }[] }
  backOrder: { recordId: string }
  consolidation: { consolidationKey: string }
  sageExport: {}
  sageUpdate: {}
  error: { details: Record<string, any>; message: string }
  update: IChange[]
  action: { name: string; body?: string; data?: Record<string, any> }
  creditLimitExceeded: {
    creditLimit: number // credit limit at the time of generating the event
    outstanding: number // outstanding balance at the time of generating the event (should include the current order)
    amountChanged: number // difference applied by this particular instance of the event (so we don't need to recalculate it later)
    resourceId: string // order/quote id etc
    resourceType: string // order/quote or w/e else adds to the outstanding balance
    currency: string // currency symbol
  }
  reportAction: {
    action: "view" | "download" | "load" | "regenerate"
    reportType: "custom" | "standard"
    id: string
    data?: Record<string, any>
  }
  customerIsNewBusinessChanged: { is_new_business: boolean }
  priceBandChange: {
    action: string
    itemMutations: IPriceBandItemChange[]
    details?: Record<string, unknown>
  }
}

export type IEventDetailsFor<T extends IEventType> = unknown extends IEventDetails[T]
  ? undefined
  : IEventDetails[T]

type ITypedEvent = { [key in IEventType]: { type: key; details: IEventDetails[key] } }[IEventType]

export type ITypedEventRecord<
  Attrs extends Exclude<keyof Models["events"], "type" | "details">,
  Type extends IEventType = IEventType,
> = IRecord<"events", Attrs> &
  ITypedEvent & {
    type: Type
  }

function hoverLoaderForResource(id: string): Record<IEventModel, React.ReactNode> {
  return {
    leads: <HoverLeadLoader id={id} />,
    branches: <HoverBranchLoader id={id} />,
    customers: <HoverCustomerLoader id={id} />,
    sub_customers: <HoverSubCustomerLoader id={id} />,
    quotes: <HoverQuoteLoader id={id} />,
    orders: <HoverOrderLoader id={id} />,
    support_tickets: null,
    support_ticket_groups: null,
    skeem_users: <HoverUserLoader id={id} />,
    contacts: <ContactLoader id={id} />,
    orderRequests: <HoverOrderRequestLoader id={id} />,
    purchase_orders: <HoverPurchaseOrderLoader id={id} />,
    projects: <HoverProjectLoader id={id} />,
    deliveries: <HoverDeliveryLoader id={id} />,
    products: <HoverProductLoader id={id} />,
    price_bands: null,
    universal_price_bands: null,
    rmas: null,
    reports: null,
    stock_transfers: null,
  }
}

export function getLinkForResource(model: IEventModel, id: string) {
  let baseRoute: eRoutes | undefined = undefined

  if (model === "customers") {
    baseRoute = eRoutes["/customers/:id"]
  }

  if (baseRoute) {
    return buildRoute(baseRoute, { id })
  } else {
    return null
  }
}

export const singularNames: Record<IEventModel, string> = {
  leads: "lead",
  branches: "branch",
  customers: "customer",
  sub_customers: "sub customer",
  quotes: "quote",
  orders: "order",
  support_tickets: "support ticket",
  support_ticket_groups: "group",
  skeem_users: "user",
  contacts: "contact",
  orderRequests: "Order Request",
  purchase_orders: "Purchase Order",
  projects: "Project",
  deliveries: "Delivery",
  rmas: "Return Request",
  products: "Product",
  price_bands: "Price Band",
  universal_price_bands: "Universal Price Band",
  reports: "Report",
  stock_transfers: "Stock Transfer",
}

export type IEvent = ITypedEventRecord<"model" | "recordId" | "createdAt"> & {
  user: IRecord<"skeem_users", "name">
}

export type ISignInEvent = ITypedEventRecord<"model" | "recordId" | "createdAt", "signIn"> & {
  user: IRecord<"skeem_users", "name">
}

export type ICreateAssociationEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "createAssociation"
> & {
  user: IRecord<"skeem_users", "name">
}
export type IUpdateAssociationEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "updateAssociation"
> & {
  user: IRecord<"skeem_users", "name">
}
export type IAssociationToEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "associateTo"
> & {
  user: IRecord<"skeem_users", "name">
}
export type IUnassociationToEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "unassociateTo"
> & {
  user: IRecord<"skeem_users", "name">
}
export type IAddNotesToEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "notes"> & {
  user: IRecord<"skeem_users", "name">
}
export type IAddUserContactedToEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "userContacted"
> & {
  user: IRecord<"skeem_users", "name">
}
export type ICloseTicketEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "ticketClosed"
> & {
  user: IRecord<"skeem_users", "name">
}

export type IMicrosoftEmailEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "emailReceived" | "emailSent"
> & {
  user: IRecord<"skeem_users", "name">
  details: { id: string }
}
export type IMicrosoftEventEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "eventCreated" | "eventInvited" | "eventUpdated"
> & {
  user: IRecord<"skeem_users", "name">
  details: { id: string; updatedKeys?: string[] }
}

export type IArchivedEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "archived"> & {
  user: IRecord<"skeem_users", "name">
  details: { archived: boolean; reason?: string }
}

export type IMergedEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "merged"> & {
  user: IRecord<"skeem_users", "name">
  details: {
    records: { id: string; name: string; details?: Record<string, string>; to?: string }[]
  }
}

export type IBackOrderEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "backOrder"> & {
  user: IRecord<"skeem_users", "name">
  details: { recordId: string }
}

export type IConsolidationEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "consolidation"
> & {
  user: IRecord<"skeem_users", "name">
  details: { consolidationKey: string }
}

export type ISageExport = ITypedEventRecord<"createdAt" | "model" | "recordId", "sageExport"> & {
  user: IRecord<"skeem_users", "name">
  details?: { data?: string | Record<string, any>; exportedBy?: string }
}
export type ISageUpdate = ITypedEventRecord<"createdAt" | "model" | "recordId", "sageUpdate"> & {
  user: IRecord<"skeem_users", "name">
  details?: {
    data?: Record<string, any>
    changes?: { type: string; description: string }[]
  }
}
export type IErrorEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "error"> & {
  user: IRecord<"skeem_users", "name">
  details: { details: Record<string, any>; message: string; reason?: string }
}

export type IActionEvent = ITypedEventRecord<"createdAt" | "model" | "recordId", "action"> & {
  user: IRecord<"skeem_users", "name">
  details: { name: IActionEventType; body?: string; data?: Record<string, any> }
}

export type ICreditLimitEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "creditLimitExceeded"
> & {
  user: IRecord<"skeem_users", "name">
  details: IEventDetails["creditLimitExceeded"]
}

export type IPriceBandChangeEvent = ITypedEventRecord<
  "createdAt" | "model" | "recordId",
  "priceBandChange"
> & {
  user: IRecord<"skeem_users", "name">
  details: IEventDetails["priceBandChange"]
}
